import { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionManager } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { Notification } from '../store/slices/notificationsSlice';
import { logger } from '../utils/logger';

export interface UseRealNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAllAsRead: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  getNotificationsByType: (type: string) => Notification[];
  fetchNotifications: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
}

export function useRealNotifications(): UseRealNotificationsReturn {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Prevent duplicate concurrent fetches
  const isFetchingRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }
    
    // Prevent duplicate concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;

    setLoading(true);
    setError(null);

    try {
      logger.debug('Notifications', `Fetching notifications for user: ${user.id}`);

      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) {
        logger.error('Notifications', 'Error fetching notifications', fetchError);
        throw fetchError;
      }

      logger.debug('Notifications', `Fetched notifications: ${data?.length || 0}`);
      
      if (!data || data.length === 0) {
        logger.debug('Notifications', 'No notifications found for this user');
        setNotifications([]);
      } else {
        setNotifications(data);
      }
    } catch (err: any) {
      logger.error('Notifications', 'Error', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [user?.id]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      logger.debug('Notifications', `Marking notification as read: ${id}`);

      const { error: updateError } = await supabase
        .from('notifications')
        .update({ 
          status: 'read', 
          read_at: new Date().toISOString() 
        })
        .eq('id', id);

      if (updateError) {
        logger.error('Notifications', 'Error marking as read', updateError);
        throw updateError;
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, status: 'read' as const, read_at: new Date().toISOString() }
            : notification
        )
      );
    } catch (err: any) {
      logger.error('Notifications', 'Error marking as read', err);
      setError(err.message || 'Failed to mark notification as read');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      logger.debug('Notifications', 'Marking all notifications as read');

      const { error: updateError } = await supabase
        .from('notifications')
        .update({ 
          status: 'read', 
          read_at: new Date().toISOString() 
        })
        .eq('recipient_id', user.id)
        .eq('status', 'unread');

      if (updateError) {
        logger.error('Notifications', 'Error marking all as read', updateError);
        throw updateError;
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          status: 'read' as const,
          read_at: notification.status === 'unread' ? new Date().toISOString() : notification.read_at
        }))
      );
    } catch (err: any) {
      logger.error('Notifications', 'Error marking all as read', err);
      setError(err.message || 'Failed to mark all notifications as read');
    }
  }, [user?.id]);

  const deleteNotification = useCallback(async (id: string) => {
    if (!id) {
      logger.error('Notifications', 'Cannot delete notification: invalid ID');
      return;
    }

    try {
      logger.debug('Notifications', `Deleting notification: ${id}`);

      // Update local state immediately for responsive UI (optimistic update)
      setNotifications(prev => {
        const notification = prev.find(n => n.id === id);
        const filtered = prev.filter(notification => notification.id !== id);
        
        logger.debug('Notifications', 'Optimistic update', {
          notificationFound: !!notification,
          before: prev.length,
          after: filtered.length
        });
        
        return filtered;
      });

      const { error: deleteError, data } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id)
        .select();

      if (deleteError) {
        logger.error('Notifications', 'Error deleting notification from DB', deleteError);
        // Revert optimistic update by refetching
        await fetchNotifications();
        throw deleteError;
      }

      logger.debug('Notifications', `Successfully deleted notification from DB: ${id}`, { rowsDeleted: data?.length || 0 });
      // Note: Real-time subscription DELETE event will also fire as a backup
      // but since we already removed it optimistically, the filter will be idempotent
    } catch (err: any) {
      logger.error('Notifications', 'Error deleting notification', err);
      setError(err.message || 'Failed to delete notification');
    }
  }, [fetchNotifications]);

  const deleteAllNotifications = useCallback(async () => {
    if (!user?.id) {
      logger.error('Notifications', 'Cannot delete all notifications: no user');
      return;
    }

    try {
      logger.debug('Notifications', `Deleting all message notifications for user: ${user.id}`);

      // Filter to only get message notifications (exclude availability requests)
      const messageNotifications = notifications.filter(n => 
        n.type === 'message'
      );

      if (messageNotifications.length === 0) {
        logger.debug('Notifications', 'No message notifications to delete');
        return;
      }

      const messageIds = messageNotifications.map(n => n.id);

      // Update local state immediately for responsive UI (optimistic update)
      // Keep all notifications except message type
      setNotifications(prev => prev.filter(n => n.type !== 'message'));

      // Delete only message notifications from database
      const { error: deleteError, data } = await supabase
        .from('notifications')
        .delete()
        .eq('recipient_id', user.id)
        .in('id', messageIds)
        .select();

      if (deleteError) {
        logger.error('Notifications', 'Error deleting message notifications from DB', deleteError);
        // Revert optimistic update by refetching
        await fetchNotifications();
        throw deleteError;
      }

      logger.debug('Notifications', `Successfully deleted message notifications from DB`, { rowsDeleted: data?.length || 0 });
      // Note: Real-time subscription DELETE events will also fire as a backup
    } catch (err: any) {
      logger.error('Notifications', 'Error deleting message notifications', err);
      setError(err.message || 'Failed to delete message notifications');
    }
  }, [user?.id, notifications, fetchNotifications]);

  const getNotificationsByType = useCallback((type: string) => {
    return notifications.filter(n => n.type === type);
  }, [notifications]);

  // Fetch notifications when user changes
  // Use InteractionManager to defer until animations complete - prevents blocking touches
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      fetchNotifications();
    });
    return () => task.cancel();
  }, [fetchNotifications]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    logger.debug('Notifications', `Setting up realtime subscription for user: ${user.id}`);

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug('Notifications', 'New notification received', payload);
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug('Notifications', 'Notification updated', payload);
          const updatedNotification = payload.new as Notification;
          setNotifications(prev => 
            prev.map(notification => 
              notification.id === updatedNotification.id 
                ? updatedNotification 
                : notification
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug('Notifications', 'Notification deleted via real-time', payload);
          const deletedId = payload.old?.id;
          if (deletedId) {
            // Use functional update to ensure we're working with the latest state
            setNotifications(prev => {
              const beforeCount = prev.length;
              const filtered = prev.filter(notification => notification.id !== deletedId);
              
              // Only update if the notification was actually in the list
              if (beforeCount !== filtered.length) {
                return filtered;
              } else {
                logger.debug('Notifications', 'Notification already removed, skipping update');
                return prev;
              }
            });
          } else {
            logger.warn('Notifications', 'DELETE event received but no old.id found', payload);
          }
        }
      )
      .subscribe((status) => {
        logger.debug('Notifications', `Subscription status: ${status}`);
      });

    return () => {
      logger.debug('Notifications', 'Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Memoize unreadCount calculation to avoid unnecessary recalculations
  const unreadCount = notifications.filter(n => n.status === 'unread').length;
  
  // Debug logging for unreadCount - disabled in production
  useEffect(() => {
    logger.debug('Notifications', `Unread count updated: ${unreadCount}/${notifications.length}`);
  }, [unreadCount, notifications.length]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAllAsRead,
    markAsRead,
    getNotificationsByType,
    fetchNotifications,
    deleteNotification,
    deleteAllNotifications,
  };
}